import React from 'react';
import { SxProps } from '@mui/system';
import { Box, Grid, TextField, Typography } from '@mui/material';

interface InputFieldProps {
  name: string;
  value?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label: JSX.Element | string;
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  description?: string;
  containerSx?: SxProps;
  boxSx?: SxProps;
  textFieldSx?: SxProps;
  typographySx?: SxProps;
  placeholder?: string;
  requiredIndicator?: JSX.Element | null;
  missingFields?: string[];
  helperText?: (fieldName: string) => string;
  showLabelAsTypography?: boolean;
  [x: string]: any;
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  value = '',
  onChange,
  label,
  flexDirection = 'column',
  description,
  containerSx,
  boxSx,
  textFieldSx,
  typographySx,
  placeholder,
  requiredIndicator,
  missingFields = [],
  helperText,
  showLabelAsTypography = true,
  ...props
}) => {
  const isError = missingFields?.includes(name);

  const commonTextFieldProps = {
    name,
    variant: "outlined" as any,
    value,
    onChange,
    sx: { backgroundColor: 'var(--white)', color: 'var(--dark)', width: '100%', ...textFieldSx },
    placeholder,
    error: isError,
    helperText: isError ? (helperText?.(name) || 'This field is required') : '',
    ...props
  };

  return (
    <Grid item sx={{ ...containerSx }}>
      <Box sx={{ display: 'flex', flexDirection, justifyContent: 'center', alignItems: 'center', width: '100%', ...boxSx }}>
        {requiredIndicator}
        {showLabelAsTypography ? (
          <>
            <Typography variant='body1' noWrap sx={{ color: 'var(--secondary)', ...typographySx }}>{label}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
              <TextField {...commonTextFieldProps} />
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
            <TextField label={label} {...commonTextFieldProps} />
          </Box>
        )}
      </Box>
      {description && (
        <Box sx={{ display: 'flex', flexDirection, justifyContent: 'center', alignItems: 'center', width: '100%', margin: '1rem 0 1rem 0', ...boxSx }}>
          {requiredIndicator}
          <Typography variant='body2' sx={{ flex: 1, color: 'var(--secondary)', textAlign: 'left' }}>{description}</Typography>
        </Box>
      )}
    </Grid>
  );
};

export default InputField;